import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import {
    MdSearch, MdHome, MdMessage, MdLogout,
    MdMenu, MdClose, MdBusinessCenter, MdNotifications, MdCheck, MdClear,
    MdWork, MdAdminPanelSettings, MdStore, MdPerson, MdLocalHospital
} from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "./firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import {
    collection, query, where, getDocs, limit, onSnapshot, doc, updateDoc, deleteDoc
} from "firebase/firestore";
import logo from "./logo.png";
import { keyframes } from "styled-components"; 
import { getMessaging } from "firebase/messaging";

const messaging = getMessaging();
const PrimaryColor = "#005b63"; 
const LightText = "#37474f"; 
const BorderColor = "#dadce0";
const slideIn = keyframes`
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
`;

const HeaderContainer = styled.header`
    display: flex; align-items: center; 
    padding: 0 15px; background-color: #f1f3f4; border-bottom: 1px solid ${BorderColor};
    height: 70px; position: sticky; top: 0; z-index: 1000;
    gap: 10px;
    width: 100%;
    box-sizing: border-box;

    @media (min-width: 768px) {
        padding: 0 30px;
        gap: 15px;
    }
`;

const MenuButton = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    color: ${PrimaryColor};
    &:hover { opacity: 0.8; }
    svg { font-size: 28px; }

    @media (min-width: 768px) {
        display: none;
    }
`;

const LogoContainer = styled.div` 
    display: flex; align-items: center; gap: 8px; cursor: pointer; flex-shrink: 0;
    
    img { 
        height: 45px; 
        width: 45px; 
        border-radius: 50%; 
        object-fit: cover;
        background-color: transparent;
    } 

    h2 { 
        font-size: 18px; color: #5c5d5e; margin: 0; 
        font-weight: 700;
        font-family: sans-serif;
        display: none; 
    } 

    @media (min-width: 1024px) {
        img { height: 50px; width: 50px; }
        h2 { display: block; font-size: 20px; }
    }
`;

const SearchWrapper = styled.div`
    position: relative;
    flex: 1;
    max-width: 450px;
    display: flex;
    align-items: center;
`;

const SearchBarContainer = styled.div`
    display: flex; align-items: center; background: white;
    padding: 6px 15px; border-radius: 30px; width: 100%;
    border: 1px solid ${BorderColor};
`;

const SearchInput = styled.input` 
    border: none; background: transparent; outline: none; width: 100%; 
    margin-left: 8px; font-size: 15px; color: #333;
    font-family: sans-serif;
    &::placeholder { color: #757575; font-weight: 500; }
`;

const SearchDropdown = styled.div`
    position: absolute; top: 55px; width: 100%; min-width: 280px;
    background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border: 1px solid ${BorderColor}; overflow: hidden; z-index: 2000;
`;

const SearchResultItem = styled.div`
    display: flex; align-items: center; gap: 12px; padding: 10px 15px; cursor: pointer;
    font-family: sans-serif;
    &:hover { background: #f8f9fa; }
    img { width: 35px; height: 35px; border-radius: 50%; object-fit: cover; border: 1px solid #eee; }
    .avatar-placeholder { 
        width: 35px; height: 35px; border-radius: 50%; background: #eee; 
        display: flex; align-items: center; justify-content: center; font-size: 12px; color: ${PrimaryColor}; font-weight: bold;
    }
    .user-info { display: flex; flex-direction: column; }
    .display-name { font-weight: 700; font-size: 14px; color: ${LightText}; }
    .category-label { font-size: 10px; color: ${PrimaryColor}; font-weight: bold; text-transform: uppercase; }
`;

const DesktopNav = styled.nav`
    display: none;
    align-items: center;
    gap: 8px;
    margin-left: auto;

    @media (min-width: 768px) {
        display: flex;
    }
`;

const DesktopNavItem = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    color: #5c5d5e; 
    cursor: pointer;
    font-size: 16px;
    font-weight: 600; 
    font-family: sans-serif;
    padding: 8px 10px;
    border-radius: 8px;
    transition: all 0.2s;
    
    svg { 
        font-size: 24px; 
        color: ${PrimaryColor}; 
    }
    
    &:hover { 
        background: rgba(0, 91, 99, 0.1);
        color: ${PrimaryColor};
    }
`;

const Badge = styled.span`
    background: #d32f2f;
    color: white;
    font-size: 10px;
    padding: 2px 5px;
    border-radius: 50%;
    font-weight: bold;
    font-family: sans-serif;
    position: absolute;
    top: 2px;
    right: 2px;
`;

const ProfileIcon = styled.div` 
    width: 42px; height: 42px; border-radius: 50%; 
    border: 2px solid ${PrimaryColor};
    display: flex; align-items: center; justify-content: center; overflow: hidden; 
    cursor: pointer; flex-shrink: 0; background: white;
    img { width: 100%; height: 100%; object-fit: cover; } 
    font-size: 16px; font-weight: bold; color: ${PrimaryColor};
    font-family: sans-serif;

    @media (max-width: 767px) {
        display: none;
    }
`;

const SideMenu = styled.div`
    position: fixed; 
    top: 0; 
    left: ${props => props.$open ? "0" : "-100%"}; 
    width: 280px; 
    height: 100vh; 
    background: white;
    box-shadow: ${props => props.$open ? "2px 0 10px rgba(0,0,0,0.1)" : "none"};
    transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 3001;
    padding: 20px;
    display: flex; 
    flex-direction: column; 
    gap: 10px;
    box-sizing: border-box;
    font-family: sans-serif;
`;

const Overlay = styled.div`
    display: ${props => props.$open ? "block" : "none"};
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5); 
    z-index: 3000;
`;

const NavItem = styled.div`
    display: flex; align-items: center; 
    color: ${LightText}; 
    cursor: pointer; gap: 15px;
    padding: 12px 15px; border-radius: 8px;
    position: relative;
    font-family: sans-serif;

    svg { 
        font-size: 24px; 
        color: ${PrimaryColor}; 
    } 

    span { 
        font-size: 16px; 
        font-weight: 500; 
    } 

    &:hover { background: #f1f3f4; color: ${PrimaryColor}; }
`;

const MobileBottomNav = styled.div`
    display: flex;
    justify-content: space-around;
    align-items: center;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 65px;
    background: white;
    border-top: 1px solid ${BorderColor};
    z-index: 2000;

    @media (min-width: 768px) {
        display: none;
    }
`;

const BottomNavItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    color: ${props => props.$active ? PrimaryColor : "#5f6368"};
    font-family: sans-serif;

    svg {
        font-size: 24px;
    }

    span {
        font-size: 12px;
        font-weight: 500;
    }
`;

const ModalOverlay = styled.div`
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 5000;
`;

const ModalContent = styled.div`
    background: white; padding: 25px; border-radius: 12px; width: 90%; max-width: 400px;
    max-height: 70vh; overflow-y: auto; box-shadow: 0 10px 30px rgba(0,0,0,0.2);
    font-family: sans-serif;
`;

const NotificationItem = styled.div`
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px; border-bottom: 1px solid #eee; gap: 10px;
    font-family: sans-serif;
    .info { display: flex; flex-direction: column; flex: 1; }
    .actions { display: flex; gap: 8px; }
`;

const ActionBtn = styled.button`
    border: none; border-radius: 6px; padding: 6px; cursor: pointer;
    background: ${props => props.accept ? "#4caf50" : "#f44336"};
    color: white; display: flex; align-items: center;
    transition: opacity 0.2s;
    &:hover { opacity: 0.8; }
`;

const AppHeader = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState("");
    const [currentFullName, setCurrentFullName] = useState("");
    const [initials, setInitials] = useState("U");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [showNotifModal, setShowNotifModal] = useState(false);
    
    const navigate = useNavigate();
    const location = useLocation();
    const dropdownRef = useRef(null);
    
    const allowedAdmins = ["support@avaami.com", "banupriya.ca23@gvncollege.edu.in"];

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser(user);
                const q = query(collection(db, "profiles"), where("uid", "==", user.uid));
                const snap = await getDocs(q);
                if (!snap.empty) {
                    const data = snap.docs[0].data();
                    setProfileImageUrl(data.profileImageUrl || "");
                    setCurrentFullName(data.fullName || "");
                    if (data.fullName) setInitials(data.fullName.charAt(0).toUpperCase());
                }

                const notifQ = query(
                    collection(db, "notifications"), 
                    where("receiverId", "==", user.uid), 
                    where("status", "==", "unread")
                );
                
                const unsubNotif = onSnapshot(notifQ, (snapshot) => {
                    const newNotifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setNotifications(newNotifs);
                });

                return () => unsubNotif();
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const handleSearch = async () => {
            if (searchQuery.trim().length >= 2) {
                const qLower = searchQuery.toLowerCase();
                
                // Search Profiles (User Full Name)
                const userQ = query(collection(db, "profiles"), limit(20));
                const userSnap = await getDocs(userQ);
                const filteredUsers = userSnap.docs
                    .map(doc => ({ 
                        ...doc.data(), 
                        searchType: 'profile',
                        displayName: doc.data().fullName,
                        displayImg: doc.data().profileImageUrl
                    }))
                    .filter(user => user.fullName?.toLowerCase().includes(qLower));

                // Search Jobs
                const jobQ = query(collection(db, "allJobs"), limit(20));
                const jobSnap = await getDocs(jobQ);
                const filteredJobs = jobSnap.docs
                    .map(doc => ({ 
                        ...doc.data(), 
                        searchType: 'job',
                        displayName: doc.data().title,
                        displayImg: null
                    }))
                    .filter(job => job.title?.toLowerCase().includes(qLower));

                // Search BusinessPages (Business Name)
                const bizQ = query(collection(db, "businessPages"), limit(20));
                const bizSnap = await getDocs(bizQ);
                const filteredBiz = bizSnap.docs
                    .map(doc => ({ 
                        ...doc.data(), 
                        searchType: 'business',
                        displayName: doc.data().businessName,
                        displayImg: doc.data().logoUrl // logoUrl field-la edukkiren
                    }))
                    .filter(biz => biz.businessName?.toLowerCase().includes(qLower));

                setSearchResults([...filteredUsers, ...filteredBiz, ...filteredJobs]);
                setShowDropdown(true);
            } else {
                setSearchResults([]);
                setShowDropdown(false);
            }
        };
        const timer = setTimeout(handleSearch, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            if (currentUser) {
                const q = query(collection(db, "users"), where("uid", "==", currentUser.uid));
                const snap = await getDocs(q);
                if (!snap.empty) {
                    await updateDoc(doc(db, "users", snap.docs[0].id), { status: "logged_out" });
                }
            }
            await signOut(auth);
            navigate("/login");
        } catch (error) {
            await signOut(auth);
            navigate("/login");
        }
    };

    const handleAccept = async (notif) => {
        try {
            const connQ = query(collection(db, "connections"), where("senderId", "==", notif.senderId), where("receiverId", "==", currentUser.uid));
            const connSnap = await getDocs(connQ);
            if (!connSnap.empty) await updateDoc(doc(db, "connections", connSnap.docs[0].id), { status: "connected" });
            await deleteDoc(doc(db, "notifications", notif.id));
        } catch (error) { console.error(error); }
    };

    const handleReject = async (notif) => {
        try {
            const connQ = query(collection(db, "connections"), where("senderId", "==", notif.senderId), where("receiverId", "==", currentUser.uid));
            const connSnap = await getDocs(connQ);
            if (!connSnap.empty) await deleteDoc(doc(db, "connections", connSnap.docs[0].id));
            await deleteDoc(doc(db, "notifications", notif.id));
        } catch (error) { console.error(error); }
    };

    return (
        <>
            <HeaderContainer>
                <MenuButton onClick={() => setIsMenuOpen(true)}>
                    <MdMenu />
                </MenuButton>

                <LogoContainer onClick={() => navigate("/Home")}>
                    <img src={logo} alt="Logo" />
                    <h2>Avaami Business Connect</h2>
                </LogoContainer>

                <SearchWrapper ref={dropdownRef}>
                    <SearchBarContainer>
                        <MdSearch size={22} color={PrimaryColor} />
                        <SearchInput 
                            placeholder="Search people, businesses..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
                        />
                    </SearchBarContainer>

                    {showDropdown && searchResults.length > 0 && (
                        <SearchDropdown>
                            {searchResults.map((item, index) => (
                                <SearchResultItem key={index} onClick={() => { 
                                    if (item.searchType === 'profile') navigate(`/profile/${encodeURIComponent(item.displayName.replace(/\s+/g, ''))}`);
                                    else if (item.searchType === 'business') navigate(`/business-view/${encodeURIComponent(item.displayName)}`);
                                    else navigate(`/jobsPage?search=${encodeURIComponent(item.displayName)}`);
                                    setShowDropdown(false); setSearchQuery(""); 
                                }}>
                                    {item.displayImg ? (
                                        <img src={item.displayImg} alt="avatar" onError={(e) => e.target.style.display='none'} />
                                    ) : (
                                        <div className="avatar-placeholder">{item.displayName?.charAt(0)}</div>
                                    )}
                                    <div className="user-info">
                                        <span className="display-name">{item.displayName}</span>
                                        <span className="category-label">{item.searchType}</span>
                                    </div>
                                </SearchResultItem>
                            ))}
                        </SearchDropdown>
                    )}
                </SearchWrapper>

                <DesktopNav>
                    <DesktopNavItem onClick={() => navigate("/Home")}><MdHome /><span>Home</span></DesktopNavItem>
                    <DesktopNavItem onClick={() => navigate("/People")}><MdBusinessCenter/><span>People</span></DesktopNavItem>
                    <DesktopNavItem onClick={() => navigate("/BusinessList")}><MdStore /><span>Business</span></DesktopNavItem>
                    <DesktopNavItem onClick={() => navigate("/jobsPage")}><MdWork /><span>Jobs</span></DesktopNavItem>
                    <DesktopNavItem onClick={() => navigate("/messages")}><MdMessage/></DesktopNavItem>
                    <DesktopNavItem onClick={() => setShowNotifModal(true)} style={{position: 'relative'}}>
                        <MdNotifications />{notifications.length > 0 && <Badge>{notifications.length}</Badge>}
                    </DesktopNavItem>
                    {currentUser && allowedAdmins.includes(currentUser.email) && (
                        <DesktopNavItem onClick={() => navigate("/Dashboard")}><MdAdminPanelSettings /></DesktopNavItem>
                    )}
                    <DesktopNavItem onClick={handleLogout}><MdLogout /></DesktopNavItem>
                </DesktopNav>

                <ProfileIcon onClick={() => navigate(`/profile/${encodeURIComponent(currentFullName.replace(/\s+/g, ''))}`)}>
                    {profileImageUrl ? <img src={profileImageUrl} alt="Profile" /> : initials}
                </ProfileIcon>
            </HeaderContainer>

            <MobileBottomNav>
                <BottomNavItem $active={location.pathname === "/Home"} onClick={() => navigate("/Home")}>
                    <MdHome />
                    <span>Home</span>
                </BottomNavItem>
                <BottomNavItem $active={location.pathname === "/People"} onClick={() => navigate("/People")}>
                    <MdBusinessCenter />
                    <span>People</span>
                </BottomNavItem>
                <BottomNavItem $active={location.pathname === "/BusinessList"} onClick={() => navigate("/BusinessList")}>
                    <MdStore />
                    <span>Business</span>
                </BottomNavItem>
                <BottomNavItem $active={location.pathname === "/jobsPage"} onClick={() => navigate("/jobsPage")}>
                    <MdWork />
                    <span>Jobs</span>
                </BottomNavItem>
                <BottomNavItem 
                    $active={location.pathname.includes("/profile/")} 
                    onClick={() => navigate(`/profile/${encodeURIComponent(currentFullName.replace(/\s+/g, ''))}`)}
                >
                    <MdPerson />
                    <span>Profile</span>
                </BottomNavItem>
            </MobileBottomNav>

            {showNotifModal && (
                <ModalOverlay onClick={() => setShowNotifModal(false)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, color: PrimaryColor }}>Notifications</h3>
                            <MdClose size={24} style={{ cursor: 'pointer' }} onClick={() => setShowNotifModal(false)} />
                        </div>
                        {notifications.map(n => (
                            <NotificationItem key={n.id}>
                                <div className="info"><strong>{n.senderFullName}</strong><span> connection request</span></div>
                                <div className="actions">
                                    <ActionBtn accept onClick={() => handleAccept(n)}><MdCheck/></ActionBtn>
                                    <ActionBtn onClick={() => handleReject(n)}><MdClear/></ActionBtn>
                                </div>
                            </NotificationItem>
                        ))}
                    </ModalContent>
                </ModalOverlay>
            )}

            <Overlay $open={isMenuOpen} onClick={() => setIsMenuOpen(false)} />
            <SideMenu $open={isMenuOpen}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
                    <MdClose size={32} style={{ cursor: 'pointer', color: PrimaryColor }} onClick={() => setIsMenuOpen(false)} />
                </div>
                <NavItem onClick={() => { navigate("/Home"); setIsMenuOpen(false); }}><MdHome /><span>Home</span></NavItem>
                <NavItem onClick={() => { navigate("/messages"); setIsMenuOpen(false); }}><MdMessage /><span>Messages</span></NavItem>
                {currentUser && allowedAdmins.includes(currentUser.email) && (
                    <NavItem onClick={() => { navigate("/Dashboard"); setIsMenuOpen(false); }}>
                        <MdAdminPanelSettings /><span>Admin Corner</span>
                    </NavItem>
                )}
                <NavItem onClick={handleLogout}><MdLogout /><span>Logout</span></NavItem>
            </SideMenu>
        </>
    );
};

export default AppHeader;